import { ref, reactive, onActivated, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete, View } from '@element-plus/icons-vue';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useWelfareActivityList() {
  const listRef = ref(null);
  const dialogVisible = ref(false);
  const dialogType = ref('add');
  const formRef = ref(null);
  const exchangeItems = ref([]);

  const searchForm = reactive({
    activityName: '',
    status: '',
    rewardType: ''
  });

  const {
    listShow,
    handleReset,
    handleSelectionChange,
    handleCurrentRowChange
  } = listCommon(searchForm);

  const FORM_DEFAULT = {
    id: '',
    activityId: '',
    activityName: '',
    rewardType: '1',
    pointsConfig: '{"0":100,"default":50}',
    itemId: '',
    itemCountPerCar: 1,
    startTime: '',
    endTime: '',
    status: '1',
    remark: ''
  };

  const formData = reactive({ ...FORM_DEFAULT });

  const formRules = {
    activityName: [
      { required: true, message: '请输入活动名称', trigger: 'blur' }
    ],
    rewardType: [
      { required: true, message: '请选择奖励类型', trigger: 'change' }
    ],
    startTime: [
      { required: true, message: '请选择开始时间', trigger: 'change' }
    ],
    endTime: [
      { required: true, message: '请选择结束时间', trigger: 'change' }
    ],
    status: [
      { required: true, message: '请选择状态', trigger: 'change' }
    ]
  };

  const loadExchangeItems = async () => {
    try {
      const res = await request.get('/exchange/item/getPage', { params: { pageNum: 1, pageSize: 1000, status: '1', exchangeType: '1' } });
      if (res.code === 200) {
        exchangeItems.value = res.data.records || [];
      }
    } catch (error) {
      console.error('加载物品列表失败', error);
    }
  };

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        activityName: searchForm.activityName,
        status: searchForm.status,
        rewardType: searchForm.rewardType
      };
      const res = await request.get('/welfare/activity/getPage', { params });
      if (listRef.value) {
        if (res.code === 200) {
          listRef.value.updateListData(res.data.records || []);
          listRef.value.updatePagination({
            pageNum: paginationParams.pageNum,
            pageSize: paginationParams.pageSize,
            total: res.data.total || 0
          });
        } else {
          ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
          listRef.value.updateListData([]);
          listRef.value.updatePagination({ ...paginationParams, total: 0 });
        }
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      if (listRef.value) {
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleAdd = async () => {
    dialogType.value = 'add';
    formRef.value?.resetFields();
    Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)));
    await loadExchangeItems();
    dialogVisible.value = true;
  };

  const handleEdit = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/welfare/activity/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.activityId = res.data.activityId || '';
      formData.activityName = res.data.activityName || '';
      formData.rewardType = res.data.rewardType || '1';
      formData.pointsConfig = res.data.pointsConfig || '{"0":100,"default":50}';
      formData.itemId = res.data.itemId || '';
      formData.itemCountPerCar = res.data.itemCountPerCar ?? 1;
      formData.startTime = res.data.startTime || '';
      formData.endTime = res.data.endTime || '';
      formData.status = res.data.status || '1';
      formData.remark = res.data.remark || '';

      await loadExchangeItems();
      dialogType.value = 'edit';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleView = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/welfare/activity/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.activityId = res.data.activityId || '';
      formData.activityName = res.data.activityName || '';
      formData.rewardType = res.data.rewardType || '1';
      formData.pointsConfig = res.data.pointsConfig || '';
      formData.itemId = res.data.itemId || '';
      formData.itemCountPerCar = res.data.itemCountPerCar ?? 1;
      formData.startTime = res.data.startTime || '';
      formData.endTime = res.data.endTime || '';
      formData.status = res.data.status || '1';
      formData.remark = res.data.remark || '';

      await loadExchangeItems();
      dialogType.value = 'view';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择数据！');
      return;
    }

    try {
      await ElMessageBox.confirm('确定删除选中的活动？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });

      listRef.value?.setLoading(true);
      const params = { id: listShow.value.id };
      const res = await request.post('/welfare/activity/delete', params);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success('删除成功');
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          ElMessage.error('删除失败：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('删除失败：网络异常');
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.value) return;

    const isValid = await new Promise((resolve) => {
      formRef.value.validate((valid) => {
        resolve(valid);
      });
    });
    if (!isValid) return;

    const submitData = {
      id: dialogType.value === 'edit' ? formData.id : '',
      activityName: formData.activityName,
      rewardType: formData.rewardType,
      pointsConfig: formData.rewardType === '1' ? formData.pointsConfig : '',
      itemId: formData.rewardType === '2' ? formData.itemId : '',
      itemCountPerCar: formData.rewardType === '2' ? formData.itemCountPerCar : 1,
      startTime: formData.startTime,
      endTime: formData.endTime,
      status: formData.status,
      remark: formData.remark
    };

    listRef.value?.setLoading(true);
    try {
      const res = await request.post('/welfare/activity/saveOrUpdate', submitData);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success(dialogType.value === 'add' ? '新增成功' : '修改成功');
          dialogVisible.value = false;
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          const tip = dialogType.value === 'add' ? '新增失败' : '修改失败';
          ElMessage.error(tip + '：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      ElMessage.error('接口异常：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  onActivated(async () => {
    if (listRef.value?.isInited) {
      const pagination = listRef.value.getPagination();
      handleLoadList(pagination);
    }
  });

  return {
    searchForm,
    listRef,
    dialogVisible,
    dialogType,
    formRef,
    listShow,
    formData,
    formRules,
    exchangeItems,
    Plus,
    Edit,
    Delete,
    View,
    handleSelectionChange,
    handleCurrentRowChange,
    handleAdd,
    handleEdit,
    handleView,
    handleDelete,
    handleLoadList,
    handleReset,
    handleSubmit
  };
}
